import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

/**
 * Formly field type that renders a list of checkboxes from an options array,
 * storing the selected values as a string array in the form control.
 *
 * Props:
 * - `options`     – `{ value: string; label: string }[]`  Available choices.
 * - `label`       – `string`  Field label.
 * - `description` – `string`  Optional helper text shown under the label.
 *
 * Form control value: `string[]` (array of selected option values).
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'selectedValues',
 *   type: 'multi-checkbox',
 *   props: {
 *     label: 'Select values',
 *     options: [{ value: 'a', label: 'Option A' }, { value: 'b', label: 'Option B' }],
 *   },
 * }
 * ```
 */
@Component({
  selector: 'app-multi-checkbox-formly-type',
  standalone: true,
  imports: [ButtonComponent, TranslatePipe],
  templateUrl: './multi-checkbox-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './multi-checkbox-formly-type.component.css',
})
export class MultiCheckboxFormlyTypeComponent extends FieldType<FieldTypeConfig> {
  get checkboxOptions(): { value: string; label: string }[] {
    return (this.props['options'] as { value: string; label: string }[]) ?? [];
  }

  get selectedValues(): string[] {
    const value = this.formControl.value;
    return Array.isArray(value) ? value : [];
  }

  isChecked(value: string): boolean {
    return this.selectedValues.includes(value);
  }

  toggle(value: string): void {
    const current = this.selectedValues;
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
  }

  selectAll(): void {
    this.formControl.setValue(this.checkboxOptions.map((o) => o.value));
    this.formControl.markAsDirty();
  }

  selectNone(): void {
    this.formControl.setValue([]);
    this.formControl.markAsDirty();
  }
}
